/**
 * Test track under a timeline: the pure half (2026-10-13; plan `.notepads/test-track-timeline.md`).
 *
 * OPBridge holds the song, plays it and keeps its beat map; the editor draws the song under the lanes. The
 * alignment rule: a timeline is five looping sections (INTRO VERSE PRE-CHORUS CHORUS BRIDGE), and the operator
 * marks which stretch of the SONG each one is (`sections` in the beat map, bars from bar 1). So under a timeline
 * section the editor draws the song stretch marked with that section's name, bar for bar - the same stretch the
 * lights were made from (`opbuttons-bot/lights/fromtrack.ts`). A section with no mark draws nothing and says so.
 *
 * Everything here is a pure function of the beat map; `testTrackStore.ts` is the thin reactive wrapper.
 */
import type { TimelineSongClip as SongClip } from '@opbuttons/schema';
import type { TestTrackBeatmap, TestTrackPeaks, TestTrackSectionMark, TestTrackTempoChoice } from '../opbridge/httpClient';

export type { SongClip };

/** The tempo the grid uses: the analysed BPM, halved or doubled by the operator's pick so it matches the clock
 *  ("i play coming undone in 150 but it's a 75 song for lighting"). */
export function gridBpm(map: Pick<TestTrackBeatmap, 'bpm' | 'tempoChoice'>): number {
  const f = map.tempoChoice === 'half' ? 0.5 : map.tempoChoice === 'double' ? 2 : 1;
  return map.bpm * f;
}

export function beatsPerBar(map: Pick<TestTrackBeatmap, 'sig'>): number {
  const n = Number(String(map.sig ?? '4/4').split('/')[0]);
  return Number.isFinite(n) && n > 0 ? n : 4;
}

/** Seconds per bar on the chosen tempo, at bar 1 (a song that changes tempo has others later). */
export function barSeconds(map: Pick<TestTrackBeatmap, 'bpm' | 'tempoChoice' | 'sig'>): number {
  return (beatsPerBar(map) * 60) / gridBpm(map);
}

// ---- the grid across tempo changes: a port of OPBridge's `opbridge-testtrack/src/grid.rs` (S318), so the
// waveform drawn under a section sits where OPBridge's clock really is. Bars count straight through a change
// (OPCut `tempo_map.py`) and each change is snapped to a whole bar. Beat 0 = bar 1; the pick scales beats.
interface Segment { startSec: number; startBeat: number; bpm: number }

function segmentsOf(map: Pick<TestTrackBeatmap, 'bpm' | 'bar1_sec' | 'tempo_changes' | 'sig'>): Segment[] {
  const bpb = beatsPerBar(map);
  const segs: Segment[] = [{ startSec: map.bar1_sec, startBeat: 0, bpm: map.bpm }];
  for (const c of map.tempo_changes ?? []) {
    // Anything but a pair is skipped, as `grid_from_beatmap` does, so this grid stays OPBridge's clock (2026-11-02:
    // the analyser wrote {at_sec, bpm} objects and destructuring one threw, leaving the song on "Loading" for ever).
    if (!Array.isArray(c)) continue;
    const [at, b] = c;
    const prev = segs[segs.length - 1]!;
    if (!(Number.isFinite(b) && b > 0 && Number.isFinite(at) && at > prev.startSec)) continue;
    const raw = prev.startBeat + ((at - prev.startSec) * prev.bpm) / 60;
    segs.push({ startSec: at, startBeat: Math.round(raw / bpb) * bpb, bpm: b });
  }
  return segs;
}
const pickFactor = (map: Pick<TestTrackBeatmap, 'tempoChoice'>) => (map.tempoChoice === 'half' ? 0.5 : map.tempoChoice === 'double' ? 2 : 1);

/** Quarter-note beats from bar 1 at file position `sec` (after the pick). */
export function beatAt(map: TestTrackBeatmap, sec: number): number {
  const segs = segmentsOf(map);
  const s = [...segs].reverse().find((x) => sec >= x.startSec) ?? segs[0]!;
  return (s.startBeat + ((sec - s.startSec) * s.bpm) / 60) * pickFactor(map);
}

/** File position of beat `beat` (after the pick); the inverse of `beatAt`. */
export function secAt(map: TestTrackBeatmap, beat: number): number {
  const segs = segmentsOf(map);
  const raw = beat / pickFactor(map);
  const s = [...segs].reverse().find((x) => raw >= x.startBeat) ?? segs[0]!;
  return s.startSec + ((raw - s.startBeat) * 60) / s.bpm;
}

/** Whole bars in the song from bar 1 to the end of the file. */
export function songBars(map: TestTrackBeatmap): number {
  return Math.max(0, Math.floor(beatAt(map, map.duration_sec) / beatsPerBar(map) + 1e-6));
}

/** Song time (s) at the START of song bar `bar` (1-based, may be fractional). */
export function songTimeAtBar(map: TestTrackBeatmap, bar: number): number {
  return secAt(map, (bar - 1) * beatsPerBar(map));
}

/** Section names are compared loosely: "PRE-CHORUS", "Pre-chorus" and "pre chorus" are one name. */
export function sameSectionName(a: string, b: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return norm(a) === norm(b);
}

export function markFor(map: TestTrackBeatmap, sectionName: string): TestTrackSectionMark | null {
  return (map.sections ?? []).find((m) => sameSectionName(m.name, sectionName)) ?? null;
}

/** One stretch of song to draw under a timeline section: timeline bars [atBar, atBar + bars) show song seconds
 *  [t0, t1). A mark shorter than its section repeats (the section loops over it); a longer one is cut. */
export interface SongSpan { atBar: number; bars: number; t0: number; t1: number }

export function spansForSection(map: TestTrackBeatmap, mark: TestTrackSectionMark, sectionStart: number, sectionBars: number): SongSpan[] {
  const len = Math.max(1, mark.toBar - mark.fromBar + 1);
  const out: SongSpan[] = [];
  for (let at = 0; at < sectionBars; at += len) {
    const bars = Math.min(len, sectionBars - at);
    out.push({ atBar: sectionStart + at, bars, t0: songTimeAtBar(map, mark.fromBar), t1: songTimeAtBar(map, mark.fromBar + bars) });
  }
  return out;
}

/** The waveform of song seconds [t0, t1) as an SVG path drawn into `w` x `h` (y = 0 at the top), one min/max
 *  column per pixel (or per peak window when a pixel holds less than one). Empty when the span has no peaks. */
export function waveformPath(peaks: TestTrackPeaks, t0: number, t1: number, w: number, h: number): string {
  const n = peaks.min.length;
  if (!n || w <= 0 || t1 <= t0) return '';
  const win = peaks.ms / 1000;
  const cols = Math.max(1, Math.round(w));
  const mid = h / 2;
  const top: string[] = [];
  const bottom: string[] = [];
  for (let c = 0; c < cols; c++) {
    const a = Math.floor((t0 + ((t1 - t0) * c) / cols) / win);
    const b = Math.max(a + 1, Math.floor((t0 + ((t1 - t0) * (c + 1)) / cols) / win));
    let lo = 0, hi = 0;
    for (let i = Math.max(0, a); i < Math.min(n, b); i++) {
      if (peaks.min[i]! < lo) lo = peaks.min[i]!;
      if (peaks.max[i]! > hi) hi = peaks.max[i]!;
    }
    const x = ((c + 0.5) * w) / cols;
    top.push(`${x.toFixed(1)},${(mid - hi * mid).toFixed(1)}`);
    bottom.push(`${x.toFixed(1)},${(mid - lo * mid).toFixed(1)}`);
  }
  return `M${top.join('L')}L${bottom.reverse().join('L')}Z`;
}

/** The marks with `name` set to song bars [fromBar, toBar] (ordered, clamped to the song, replacing that name's
 *  old mark). Other names keep theirs, even when they overlap: one song stretch may light two sections. */
export function setMark(map: TestTrackBeatmap, name: string, fromBar: number, toBar: number): TestTrackSectionMark[] {
  const last = Math.max(1, songBars(map));
  const a = Math.max(1, Math.min(last, Math.round(Math.min(fromBar, toBar))));
  const b = Math.max(a, Math.min(last, Math.round(Math.max(fromBar, toBar))));
  const rest = (map.sections ?? []).filter((m) => !sameSectionName(m.name, name));
  return [...rest, { name, fromBar: a, toBar: b }];
}

// ---- clips (2026-10-13, operator: "trim and split a song by bar / beatgrid"; per timeline, split = cut into clips
// you can move, drop or repeat, snapped to bars or beats). A clip says: at timeline beat `atBeat`, play the song from
// its beat `fromBeat` for `beats`. Clips are ordered and never overlap; a gap is silence. Beats are the song grid's
// (after the tempo pick), so one timeline bar = `beatsPerBar(map)` beats, as OPBridge's clock counts them.

/** The song's playable beats: from the file's start (negative, the intro before bar 1) to its end. */
export function clipBounds(map: TestTrackBeatmap): { min: number; max: number } {
  return { min: beatAt(map, 0), max: beatAt(map, map.duration_sec) };
}

const ordered = (clips: readonly SongClip[]) => [...clips].sort((a, b) => a.atBeat - b.atBeat);
const EPS = 1e-6;

/** What the section marks play today, written out as clips: each marked section's stretch (repeated when the mark
 *  is shorter than the section, cut when longer); an unmarked section carries the song on from where it was, as
 *  OPBridge plays it; with no marks at all, the song runs straight from bar 1. The first edit starts from this. */
export function clipsFromMarks(map: TestTrackBeatmap, sections: ReadonlyArray<{ name: string; start: number; bars: number }>, newId: () => string): SongClip[] {
  const bpb = beatsPerBar(map);
  const { max } = clipBounds(map);
  const out: SongClip[] = [];
  let songAt = 0;
  // A lead-in (the first section starting after bar 0, `firstSectionBar`): the song plays from its start there, as
  // an unmarked section does, and carries on into the first section unless that one is marked.
  const first = sections[0];
  const all = first && first.start > EPS ? [{ name: '', start: 0, bars: first.start }, ...sections] : sections;
  for (const sec of all) {
    const mk = sec.name ? markFor(map, sec.name) : undefined;
    if (mk) {
      const len = Math.max(1, mk.toBar - mk.fromBar + 1);
      for (let at = 0; at < sec.bars; at += len) {
        const bars = Math.min(len, sec.bars - at);
        out.push({ id: newId(), atBeat: (sec.start + at) * bpb, fromBeat: (mk.fromBar - 1) * bpb, beats: bars * bpb });
        songAt = (mk.fromBar - 1 + bars) * bpb;
      }
    } else {
      const beats = Math.min(sec.bars * bpb, max - songAt);
      if (beats > EPS) out.push({ id: newId(), atBeat: sec.start * bpb, fromBeat: songAt, beats });
      songAt += Math.max(0, beats);
    }
  }
  return mergeTouching(out);
}

/** What the song row shows before the first edit: the marks written out as clips with stable ids (`m0`, `m1`, ...),
 *  so a selection survives a redraw and the first edit keeps the clip it was made on. */
export function derivedClips(map: TestTrackBeatmap, sections: ReadonlyArray<{ name: string; start: number; bars: number }>): SongClip[] {
  let n = 0;
  return clipsFromMarks(map, sections, () => `m${n++}`);
}

/** Neighbouring clips that continue each other (the song running straight on) become one clip. */
function mergeTouching(clips: SongClip[]): SongClip[] {
  const out: SongClip[] = [];
  for (const c of ordered(clips)) {
    const p = out[out.length - 1];
    if (p && Math.abs(p.atBeat + p.beats - c.atBeat) < EPS && Math.abs(p.fromBeat + p.beats - c.fromBeat) < EPS) p.beats += c.beats;
    else out.push({ ...c });
  }
  return out;
}

/** The clip under timeline beat `beat`, if any. */
export function clipAt(clips: readonly SongClip[], beat: number): SongClip | null {
  return clips.find((c) => beat >= c.atBeat - EPS && beat < c.atBeat + c.beats - EPS) ?? null;
}

/** Cut clip `id` in two at timeline beat `at`; null when `at` is not strictly inside it. */
export function splitClip(clips: readonly SongClip[], id: string, at: number, newId: () => string): SongClip[] | null {
  const c = clips.find((x) => x.id === id);
  if (!c || at <= c.atBeat + EPS || at >= c.atBeat + c.beats - EPS) return null;
  const head = at - c.atBeat;
  return ordered([
    ...clips.filter((x) => x.id !== id),
    { ...c, beats: head },
    { id: newId(), atBeat: at, fromBeat: c.fromBeat + head, beats: c.beats - head },
  ]);
}

/** Trim clip `id`'s start or end edge to timeline beat `to`, keeping at least `minBeats`, inside the song's file and
 *  short of its neighbours. A start trim moves where the clip begins AND which music it begins on (the music under
 *  the rest of the clip stays put, as trimming a clip in a DAW). */
export function trimClip(map: TestTrackBeatmap, clips: readonly SongClip[], id: string, edge: 'start' | 'end', to: number, minBeats: number): SongClip[] {
  const list = ordered(clips);
  const i = list.findIndex((x) => x.id === id);
  if (i < 0) return list;
  const c = list[i]!;
  const { min, max } = clipBounds(map);
  const end = c.atBeat + c.beats;
  if (edge === 'start') {
    const lo = Math.max(i > 0 ? list[i - 1]!.atBeat + list[i - 1]!.beats : 0, c.atBeat - (c.fromBeat - min));
    const at = Math.min(end - minBeats, Math.max(lo, to));
    list[i] = { ...c, atBeat: at, fromBeat: c.fromBeat + (at - c.atBeat), beats: end - at };
  } else {
    const hi = Math.min(i + 1 < list.length ? list[i + 1]!.atBeat : Infinity, c.atBeat + (max - c.fromBeat));
    const e = Math.max(c.atBeat + minBeats, Math.min(hi, to));
    list[i] = { ...c, beats: e - c.atBeat };
  }
  return list;
}

/** Move clip `id` to start at timeline beat `to` (>= 0). Where that overlaps another clip it goes to the nearest
 *  place that fits; when nothing fits, it stays. */
export function moveClip(clips: readonly SongClip[], id: string, to: number): SongClip[] {
  const c = clips.find((x) => x.id === id);
  if (!c) return ordered(clips);
  const others = ordered(clips.filter((x) => x.id !== id));
  // The gaps between the other clips, then the nearest start inside one that holds the clip.
  let best: number | null = null;
  let prevEnd = 0;
  for (let k = 0; k <= others.length; k++) {
    const gapEnd = k < others.length ? others[k]!.atBeat : Infinity;
    if (gapEnd - prevEnd >= c.beats - EPS) {
      const at = Math.min(gapEnd - c.beats, Math.max(prevEnd, to));
      if (best === null || Math.abs(at - to) < Math.abs(best - to)) best = at;
    }
    if (k < others.length) prevEnd = others[k]!.atBeat + others[k]!.beats;
  }
  return ordered([...others, { ...c, atBeat: best ?? c.atBeat }]);
}

export function deleteClip(clips: readonly SongClip[], id: string): SongClip[] {
  return ordered(clips.filter((x) => x.id !== id));
}

/** A copy of clip `id` straight after it (the same music again); null when the next clip leaves no room. */
export function duplicateClip(clips: readonly SongClip[], id: string, newId: () => string): SongClip[] | null {
  const list = ordered(clips);
  const i = list.findIndex((x) => x.id === id);
  if (i < 0) return null;
  const c = list[i]!;
  const at = c.atBeat + c.beats;
  const next = list[i + 1];
  if (next && next.atBeat < at + c.beats - EPS) return null;
  return ordered([...list, { ...c, id: newId(), atBeat: at }]);
}

/** A new clip at timeline beat `at` in a gap: `beats` long (shortened to the gap), playing the music that lines up
 *  with the clip before it (the song carrying on), or from bar 1 when there is none. Null when `at` is in a clip. */
export function addClip(map: TestTrackBeatmap, clips: readonly SongClip[], at: number, beats: number, newId: () => string): SongClip[] | null {
  if (clipAt(clips, at)) return null;
  const list = ordered(clips);
  const prev = [...list].reverse().find((x) => x.atBeat + x.beats <= at + EPS);
  const next = list.find((x) => x.atBeat > at);
  const { min, max } = clipBounds(map);
  const fromBeat = Math.max(min, prev ? prev.fromBeat + (at - prev.atBeat) : 0);
  const len = Math.min(beats, (next ? next.atBeat : Infinity) - at, max - fromBeat);
  if (len <= EPS) return null;
  return ordered([...list, { id: newId(), atBeat: at, fromBeat, beats: len }]);
}

// ---- Ableton-style editing (2026-10-13, operator: "i want the handling of audio clip to be similar to ableton"):
// a clip moved, trimmed or duplicated onto others OVERWRITES what it lands on (the clip underneath is trimmed, split
// or removed), as in Live's Arrangement; several clips move together; Alt-drag slips the audio inside a clip.

/** Put `clip` onto the list, cutting away whatever part of any other clip it covers. */
export function placeOver(clips: readonly SongClip[], clip: SongClip, newId: () => string): SongClip[] {
  const a = clip.atBeat; const b = clip.atBeat + clip.beats;
  const out: SongClip[] = [];
  for (const o of clips) {
    const oa = o.atBeat; const ob = o.atBeat + o.beats;
    if (ob <= a + EPS || oa >= b - EPS) { out.push(o); continue; }
    if (oa < a - EPS) out.push({ ...o, beats: a - oa });
    if (ob > b + EPS) out.push({ ...o, id: oa < a - EPS ? newId() : o.id, atBeat: b, fromBeat: o.fromBeat + (b - oa), beats: ob - b });
  }
  out.push(clip);
  return ordered(out);
}

/** Move the clips `ids` by `delta` timeline beats together (the earliest stops at beat 0), overwriting what they
 *  land on. */
export function moveClipsOver(clips: readonly SongClip[], ids: readonly string[], delta: number, newId: () => string): SongClip[] {
  const moving = clips.filter((c) => ids.includes(c.id));
  if (!moving.length) return ordered(clips);
  const d = Math.max(delta, -Math.min(...moving.map((c) => c.atBeat)));
  let out = clips.filter((c) => !ids.includes(c.id));
  for (const c of ordered(moving)) out = placeOver(out, { ...c, atBeat: c.atBeat + d }, newId);
  return out;
}

/** How far clip `c`'s music sits off the timeline's bar lines, in beats (2026-10-16, operator: a clip slid to the
 *  start drifted out of time with no warning). The song's bar lines fall on the timeline's where `atBeat - fromBeat`
 *  is a whole number of bars. 0 = in time; otherwise the nearest offset, between -bar/2 and +bar/2. */
export function clipOffBeat(c: Pick<SongClip, 'atBeat' | 'fromBeat'>, beatsPerBar: number): number {
  const p = c.atBeat - c.fromBeat;
  const r = ((p % beatsPerBar) + beatsPerBar) % beatsPerBar;
  const d = r > beatsPerBar / 2 ? r - beatsPerBar : r;
  return Math.abs(d) < 1e-6 || Math.abs(Math.abs(d) - beatsPerBar) < 1e-6 ? 0 : d;
}

/** A move of the clips `ids` by about `want` timeline beats that lands `lead`'s MUSIC on the grid (`grid` beats: a
 *  bar, or a beat): the song's bar lines on the timeline's, not the clip's left edge, so a clip starting partway into
 *  the intro still keeps time. Never puts the earliest moving clip before beat 0: it stops at the first in-time
 *  place after it instead of being clamped out of time. */
export function inTimeDelta(clips: readonly SongClip[], ids: readonly string[], lead: SongClip, want: number, grid: number): number {
  const moving = clips.filter((c) => ids.includes(c.id));
  const minAt = moving.length ? Math.min(...moving.map((c) => c.atBeat)) : lead.atBeat;
  const phase = lead.atBeat - lead.fromBeat;
  let d = Math.round((phase + want) / grid) * grid - phase;
  while (minAt + d < -1e-6) d += grid;
  return d;
}

/** Trim clip `id`'s edge to timeline beat `to`, kept inside the song's file and at least `minBeats` long; it
 *  overwrites a neighbour it grows over. */
export function trimClipOver(map: TestTrackBeatmap, clips: readonly SongClip[], id: string, edge: 'start' | 'end', to: number, minBeats: number, newId: () => string): SongClip[] {
  const c = clips.find((x) => x.id === id);
  if (!c) return ordered(clips);
  const { min, max } = clipBounds(map);
  const end = c.atBeat + c.beats;
  let next: SongClip;
  if (edge === 'start') {
    const at = Math.min(end - minBeats, Math.max(0, c.atBeat - (c.fromBeat - min), to));
    next = { ...c, atBeat: at, fromBeat: c.fromBeat + (at - c.atBeat), beats: end - at };
  } else {
    const e = Math.max(c.atBeat + minBeats, Math.min(c.atBeat + (max - c.fromBeat), to));
    next = { ...c, beats: e - c.atBeat };
  }
  return placeOver(clips.filter((x) => x.id !== id), next, newId);
}

/** Slip the audio inside the clips `ids` by `delta` song beats: they stay where they are and play earlier (negative)
 *  or later music, kept inside the file (a clip longer than the file still starts inside it). */
export function slipClip(map: TestTrackBeatmap, clips: readonly SongClip[], ids: string | readonly string[], delta: number): SongClip[] {
  const { min, max } = clipBounds(map);
  const set = typeof ids === 'string' ? [ids] : ids;
  return ordered(clips.map((c) => (set.includes(c.id) ? { ...c, fromBeat: Math.max(min, Math.min(max - c.beats, c.fromBeat + delta)) } : c)));
}

/** Trim the same edge of every clip in `ids` by `delta` timeline beats (Live trims every selected clip together). */
export function trimClipsOver(map: TestTrackBeatmap, clips: readonly SongClip[], ids: readonly string[], edge: 'start' | 'end', delta: number, minBeats: number, newId: () => string): SongClip[] {
  let out: SongClip[] = [...clips];
  for (const id of ids) {
    const c = out.find((x) => x.id === id);
    if (!c) continue;
    out = trimClipOver(map, out, id, edge, edge === 'start' ? c.atBeat + delta : c.atBeat + c.beats + delta, minBeats, newId);
  }
  return out;
}

/** Paste copies of `copied` so the earliest starts at timeline beat `at`, overwriting what is there. Returns the
 *  list and the pasted ids (the new selection). */
export function pasteClipsAt(clips: readonly SongClip[], copied: readonly SongClip[], at: number, newId: () => string): { clips: SongClip[]; ids: string[] } | null {
  if (!copied.length) return null;
  const d = at - Math.min(...copied.map((c) => c.atBeat));
  let out: SongClip[] = [...clips];
  const ids: string[] = [];
  for (const c of ordered(copied)) { const id = newId(); ids.push(id); out = placeOver(out, { ...c, id, atBeat: Math.max(0, c.atBeat + d) }, newId); }
  return { clips: out, ids };
}

/** The grid clips are written on: the tempo pick and bar 1. */
export interface ClipGrid { tempoChoice: TestTrackTempoChoice; bar1Sec: number }
export const gridOf = (m: TestTrackBeatmap): ClipGrid => ({ tempoChoice: m.tempoChoice ?? 'as-read', bar1Sec: m.bar1_sec });

/** Clips written on `grid`, as the song's grid is NOW: each keeps the same music (song seconds), and the whole
 *  arrangement scales with a changed tempo pick so the clips keep their places relative to each other. Clips with
 *  no recorded grid (older) are taken as written. */
export function effectiveClips(clips: readonly SongClip[], grid: ClipGrid | undefined, map: TestTrackBeatmap): SongClip[] {
  if (!grid) return [...clips];
  const now = gridOf(map);
  if (now.tempoChoice === grid.tempoChoice && Math.abs(now.bar1Sec - grid.bar1Sec) < 1e-9) return [...clips];
  const old: TestTrackBeatmap = { ...map, tempoChoice: grid.tempoChoice, bar1_sec: grid.bar1Sec };
  const r = pickFactor(map) / pickFactor(old);
  return clips.map((c) => {
    const s0 = secAt(old, c.fromBeat); const s1 = secAt(old, c.fromBeat + c.beats);
    const from = beatAt(map, s0);
    return { ...c, atBeat: c.atBeat * r, fromBeat: from, beats: Math.max(1e-6, beatAt(map, s1) - from) };
  });
}

/** Split at timeline beat `at`: the clips `ids` that contain it, or, with none given, the clip under it. Null when
 *  nothing was cut. */
export function splitClipsAt(clips: readonly SongClip[], ids: readonly string[], at: number, newId: () => string): SongClip[] | null {
  // The selected clips that exist; with none (nothing selected, or a selection an undo removed), the one under it.
  const listed = clips.filter((c) => ids.includes(c.id));
  const targets = listed.length ? listed : [clipAt(clips, at)].filter((c): c is SongClip => !!c);
  let out: SongClip[] = [...clips];
  let cut = false;
  for (const c of targets) { const r = splitClip(out, c.id, at, newId); if (r) { out = r; cut = true; } }
  return cut ? ordered(out) : null;
}

/** Duplicate the clips `ids` straight after the stretch they cover (Live's Cmd+D), overwriting what is there.
 *  Returns the new list and the copies' ids (the new selection). */
export function duplicateClipsAfter(clips: readonly SongClip[], ids: readonly string[], newId: () => string): { clips: SongClip[]; ids: string[] } | null {
  const sel = clips.filter((c) => ids.includes(c.id));
  if (!sel.length) return null;
  const span = Math.max(...sel.map((c) => c.atBeat + c.beats)) - Math.min(...sel.map((c) => c.atBeat));
  let out: SongClip[] = [...clips];
  const copies: string[] = [];
  for (const c of ordered(sel)) { const id = newId(); copies.push(id); out = placeOver(out, { ...c, id, atBeat: c.atBeat + span }, newId); }
  return { clips: out, ids: copies };
}

export function deleteClips(clips: readonly SongClip[], ids: readonly string[]): SongClip[] {
  return ordered(clips.filter((c) => !ids.includes(c.id)));
}

/** Song seconds a clip plays, for drawing its waveform. */
export function clipSeconds(map: TestTrackBeatmap, c: SongClip): { t0: number; t1: number } {
  return { t0: Math.max(0, secAt(map, c.fromBeat)), t1: Math.min(map.duration_sec, secAt(map, c.fromBeat + c.beats)) };
}

/** Slide the marks named in `names` by `deltaBars` song bars (negative = earlier in the song), each keeping its
 *  length, clamped so every mark stays inside the song (operator, 2026-10-13: "move the audio around, at least left
 *  and right bar quantized"). Dragging the waveform RIGHT under a section shows EARLIER music there, so the caller
 *  passes minus the bars dragged. Other marks are untouched. */
export function shiftMarks(map: TestTrackBeatmap, names: string[], deltaBars: number): TestTrackSectionMark[] {
  const last = Math.max(1, songBars(map));
  const hit = (m: TestTrackSectionMark) => names.some((n) => sameSectionName(n, m.name));
  const marks = map.sections ?? [];
  // One shift for all of them, the largest that keeps each inside the song, so a group keeps its spacing.
  let d = Math.round(deltaBars);
  for (const m of marks.filter(hit)) {
    d = Math.max(d, 1 - m.fromBar);
    d = Math.min(d, last - m.toBar);
  }
  return marks.map((m) => (hit(m) ? { ...m, fromBar: m.fromBar + d, toBar: m.toBar + d } : m));
}
